import { queryMarkerNamesUsed } from '../queryParameters/queryMarkers';

/**
 * The parameters a report run can supply values for.
 *
 * A fixed set, and the same one for every saved query, which is a stand-in for parameters declared
 * per query and stored alongside its SQL. Until that exists this list is what "a legal parameter"
 * means, so it is also what a query is checked against — see assertReportQueryParametersValid in
 * shared-node.
 */
export const REPORT_QUERY_PARAMETER_NAMES = ['report_from_date', 'report_to_date', 'report_as_of_date'] as const;

export type ReportQueryParameterName = (typeof REPORT_QUERY_PARAMETER_NAMES)[number];

export const isReportQueryParameterName = (name: string): name is ReportQueryParameterName =>
  (REPORT_QUERY_PARAMETER_NAMES as readonly string[]).includes(name);

/** Deduped report parameters the SQL uses, in the order this set declares them. */
export const reportQueryParametersUsed = (sql: string): ReportQueryParameterName[] => {
  const used = new Set(queryMarkerNamesUsed(sql));
  return REPORT_QUERY_PARAMETER_NAMES.filter((name) => used.has(name));
};

/**
 * Which of a run window's two dates the SQL takes. The period start feeds `{{report_from_date}}`;
 * the period end feeds both `{{report_to_date}}` and `{{report_as_of_date}}`, because an as-of
 * query is "as of" the moment its period closed.
 */
export const reportQueryWindowNeeds = (sql: string): { startDate: boolean; endDate: boolean } => {
  const used = reportQueryParametersUsed(sql);
  return {
    startDate: used.includes('report_from_date'),
    endDate: used.includes('report_to_date') || used.includes('report_as_of_date'),
  };
};
